import React, { createContext, useState, useEffect, useContext } from 'react'
import { NotificationProvider, NotificationContext } from 'Contexts/NotificationContext/NotificationContext'
import CookieReader from 'Utilities/CookieUtilties/CookieReader'
import { GetUserGuid, GetUserVin, IsUserBot } from 'Utilities/CookieUtilties/UserCookieUtilities'
import SetCookie from 'Utilities/CookieUtilties/SetCookie'
import useSWR from 'swr'
import Event from 'Utilities/Event'
import { ThemeContext } from 'Contexts/ThemeContext'
import { InitLogRocket, IdentifyLogRocket, InitLogRocketv2 } from 'Contexts/UserContext/InitLogRocket.js'
import { UpdateUserProfile } from 'Contexts/UserContext/UpdateUserProfile.js'
import { StorageAccessor } from 'Utilities/LocalStorageUtility'
import Retry from 'Utilities/Retry'
import { saveLocation } from "Utilities/Location/Location"
import useFeatureFlag from '../../Hooks/useFeatureFlag/useFeatureFlag'
import { getUserAudiences } from './UserRequests'
import useGlobalPrivacyControl from "Hooks/useGlobalPrivacyControl";

import useLogRocket from 'Hooks/useLogRocket';

InitLogRocketv2()

// IsRegistered
//  -   is a special null case where we need to know after we get a response back form the server whether or not to keep making calls for user information
//      so it must be null and set accordingly
const initialState = {
    hydrated: false,
    emailAddress: null,
    zipCode: null,
    firstName: null,
    lastName: null,
    userGuid: null,
    userName: null,
    userVin: function () { return GetUserVin() },
    getGuid: function () { return GetUserGuid() },
    isBot: function () { return IsUserBot() },
    isRegistered: null,
    isInternational: null,
    sopCustomerId: null,
    isSignedUpForEmails: null,
    isCustomerInternal: null,
    isLoyaltyBetaUser: false,
    isLoyaltyRegisteredUser: false,
    logRocketInitialized: false,
    logRocketIdentified: false,
    isSpecialPriceEligible: false,
    isEmployee: function () { return (CookieReader('ExcludeIpFromAnalytics') || CookieReader('ExcludeEmailFromAnalytics')) }
}

const UserContext = createContext(initialState)
const { Provider } = UserContext

const UserProvider = ({ children, userGuid, updateUserGuid, stateObject, services }) => {

    const { setIsLoading } = useContext(ThemeContext)
    const [context, setContext] = useState(initialState)
    const { isGpcEnabled } = useGlobalPrivacyControl()
    const { data: userProfile, mutate: userProfileMutate, error: userProfileError } = useSWR(context.isBot() ? null : ['userProfile', services?.customer?.url, context.getGuid()], UpdateUserProfile)
    const { data : userAudiences } = useSWR(context.isBot() ? null : ['userAudience', services?.customer?.url, context.getGuid()], (_, customerUrl, webUserGuid) => getUserAudiences(customerUrl, webUserGuid))
    const { audiences, googleAudiences } = userAudiences??{}; 
    const { PushNotification } = useContext(NotificationContext)
    const { logRocketTrack } = useLogRocket()
    const gtagCacheKey = "sentGtagSegmentAudiences"

    useEffect(() => {
        if (audiences)
            SetCookie("segment_audiences", JSON.stringify(audiences), 365);
        setPromoCodeFromUrl();
        showFreeShippingToast();
    }, [audiences])

    useEffect(() => {

        try {
            for(let ga of googleAudiences??[]) {

                let sentEventsValues = StorageAccessor.sessionStorage.getObj(gtagCacheKey)??[];
                if(!sentEventsValues.includes(ga)) {
                    gtag('event', 'segment_google_audiences', {
                        'google_audience_name': `${ga}`
                    })
                    sentEventsValues.push(ga)
                    StorageAccessor.sessionStorage.setObj(gtagCacheKey, sentEventsValues);
                }               
            }      
        } catch (e) {
            console.error('unable to send google gtag event')
        }
        
    }, [googleAudiences])

    if (context.hydrated && (context.getGuid() != userGuid)) {
        updateUserGuid(context.getGuid())
    }

    if (userProfile && !context.hydrated) {
        setContext((state) => {
            return { ...state, ...userProfile, hydrated: true }
        })
    }


    let lrEnabled = useFeatureFlag("Is_Log_Rocket_Enabled");


    useEffect(() => {
        if (context.logRocketInitialized) {
            try {
                let messageQueue = JSON.parse(window.localStorage.getItem("errorStorage"));

                if (messageQueue?.length) {
                    console.log(" === Following errors from local storage trap");
                    for (let i = 0, ilen = messageQueue.length; i < ilen; i++) {
                        console.error(JSON.stringify(messageQueue[i]));
                    }

                }

                window.localStorage.removeItem("errorStorage");
            } catch (err) {
                console.error("Error attempting to read from error trap", err)
            }

        }
    }, [context.logRocketInitialized])

    useEffect(() => {

        if (!isGpcEnabled && context.hydrated && !context.isBot() && lrEnabled && context.logRocketInitialized === false) {

            let nextEnvLogRocketId = process.env.NEXT_PUBLIC_LOGROCKET_APP_ID
            let logRocketAppId = nextEnvLogRocketId || stateObject?.logRocketAppId

            setContext((state) => {
                //null used to indicate "in progress" !!!
                return { ...state, logRocketInitialized: null }
            })
            InitLogRocket(logRocketAppId, stateObject?.releaseVersion).then(() => {
                setContext((state) => {
                    return { ...state, logRocketInitialized: true }
                })
            });

            if(userProfile?.isLoyaltyBetaUser) {
                logRocketTrack("Loyalty_BetaUser")
            }
        }

        if (context.logRocketInitialized && !context.logRocketIdentified && context.hydrated) {
            IdentifyLogRocket(context.userGuid, context.emailAddress, context.userVin(), context.sopCustomerId)
            setContext((state) => {
                return { ...state, logRocketIdentified: true }
            })
        }
    }, [isGpcEnabled, context.hydrated, context.isBot(), lrEnabled, context.logRocketInitialized])


    const updateUserProfile = async () => {
        userProfileMutate();
    }

    const logoutUser = async () => {
        try {
            const module = await Retry(() => import('Contexts/UserContext/LogoutUser'))
            const response = await module.LogoutUser(setIsLoading)
            if (response != null) {
                setContext({
                    ...initialState,
                    redirectUrl: response.redirectUrl,
                    hydrated: true
                })
            } else {
                setContext({
                    ...initialState,
                    hydrated: true
                })
                setIsLoading(false)
            }

            //
            try {
                analytics.track('Logout', {})
            } catch (e) {
                console.error(e)
            }
        } catch (err) {
            console.error(err)
        }

    }

    useEffect(() => {
        if (context.redirectUrl && typeof window !== "undefined") {
            window.location = context.redirectUrl;
        }
    }, [context.redirectUrl]);

    const updateUserZipCode = (zipCode) => {
        saveLocation(zipCode, null)
        setContext((state) => {
            const newState = {
                ...state,
                zipCode: zipCode,
                hydrated: true
            }

            return newState
        })
    }

    const updateUserIsInternational = (isInternational) => {
        saveLocation(null, isInternational)
        setContext((state) => {
            const newState = {
                ...state,
                isInternational: isInternational,
                hydrated: true
            }

            return newState
        })
    }

    const setPromoCodeFromUrl = () => {

        if (windowIsDefined()) {

            let searchParams = new URLSearchParams(window.location.search)
            let promoCodeFromUrl = searchParams.get('smi_pc')

            if (promoCodeIsValid(promoCodeFromUrl) && promoCodeIsNotAlreadyApplied(promoCodeFromUrl)) {
                SetCookie("PromoCode", promoCodeFromUrl, 1);
                PushNotification(`${promoCodeFromUrl} has been applied!`);
            }
        }

        function promoCodeIsNotAlreadyApplied(promoCodeFromUrl) {
            return !document.cookie.includes(promoCodeFromUrl);
        }

        function windowIsDefined() {
            return typeof window !== 'undefined';
        }

        function promoCodeIsValid(promoCode) {
            return promoCode && promoCode.trim() !== "";
        }
    };

    let enableShippingToast = useFeatureFlag("Free_Shipping_Toast");

    const showFreeShippingToast = () => {
        if (enableShippingToast) {
            let cookieVal = 'freeShippingAdvertised';
            if (!document.cookie.includes(cookieVal) && PushNotification !== undefined) {
                // checking if PushNotification is undefined in case UserContext is not wrapped inside a Notification Provider.
                SetCookie("FreeShippingToast", cookieVal, 1);
                PushNotification("Free Shipping over $149", //if you change this, change DismissNotification("") in SatyDropdown
                    "positive", 10000, false,
                    { icon: "fast_shipping", intent: "positive", size: 1.25 });
            }
        }

    }

    return (
        <Provider
            value={{
                context: { ...context, userGuid: userGuid },
                updateUserGuid,
                updateUserProfile,
                logoutUser,
                updateUserZipCode,
                updateUserIsInternational,
            }}
        >
            {children}
        </Provider>
    )
}

export { UserProvider, UserContext }
